import React, { useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom"; // ✅ Use useHistory and useLocation for React Router v5
import axios from "axios";
import "./Register.css";
import Toast from "./Toast";

export default function Register() {
  const history = useHistory(); // ✅ Initialize history
  const location = useLocation(); // ✅ Initialize location

  const verifiedPhone = location.state?.verifiedPhone || "";

  // Protect route: Redirect to Welcome if no verified phone number
  React.useEffect(() => {
    if (!location.state?.verifiedPhone) {
      history.replace('/');
    }
  }, [history, location]);

  const [formData, setFormData] = useState({
    field: "photography",
    studio_name: "",
    name: "",
    email: "",
    phone: verifiedPhone,
    state: "",
    gst: "",
  });

  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, type: "info", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreditsPopup, setShowCreditsPopup] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Allow only digits and up to 10 characters
    if (/^\d{0,10}$/.test(value)) {
      setFormData((prevData) => ({
        ...prevData,
        phone: value,
      }));
      // Clear error when user starts typing
      if (errors.phone) {
        setErrors((prevErrors) => ({ ...prevErrors, phone: "" }));
      }
    }
  };

  const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  const handleGstChange = (e) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (raw.length > 15) return;
    setFormData((prev) => ({ ...prev, gst: raw }));

    if (!raw) {
      setErrors((prev) => ({ ...prev, gst: "" }));
    } else if (raw.length < 15) {
      setErrors((prev) => ({ ...prev, gst: `${15 - raw.length} more character${15 - raw.length > 1 ? "s" : ""} needed` }));
    } else if (!GST_REGEX.test(raw)) {
      setErrors((prev) => ({ ...prev, gst: "Invalid GST format (e.g. 22ABCDE1234F1Z5)" }));
    } else {
      setErrors((prev) => ({ ...prev, gst: "" }));
    }
  };


  const validateForm = () => {
    let newErrors = {};
    
    // Studio name is required
    if (!formData.studio_name.trim()) {
      newErrors.studio_name = "Studio name is required";
    }
    
    // Name is required
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    // Email is required and must be valid
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    // Phone is required
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    // State is required
    if (!formData.state) {
      newErrors.state = "State is required";
    }

    // GST is optional but must match format if provided
    if (formData.gst && !GST_REGEX.test(formData.gst)) {
      newErrors.gst = "Enter a valid GST number (e.g. 22ABCDE1234F1Z5)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const API_KEY = process.env.REACT_APP_BROWSER_API_KEY;
      if (!API_KEY) {
        setToast({ open: true, type: 'error', message: 'Missing REACT_APP_BROWSER_API_KEY. Add it to .env.development and restart.' });
        setIsSubmitting(false);
        return;
      }

      // Call vendor creation API
      const payload = {
        field: formData.field,
        studio_name: formData.studio_name,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        state: formData.state,
        ...(formData.gst && { gst: formData.gst }),
        ...(logoUrl && { studio_logo: logoUrl }),
      };
      
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(
        `${baseURL}/vendors`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_BROWSER_API_KEY
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Store vendor info and automatically log in
        const userData = data.userData || data;
        const vendorId = userData.vendor_id;
        const firstName = userData.first_name || '';
        const lastName = userData.last_name || '';
        const displayName = `${firstName} ${lastName}`.trim() || formData.name.trim() || 'Vendor';

        try {
          localStorage.setItem('vendorDisplayName', displayName);
          if (vendorId) localStorage.setItem('vendorId', vendorId);
          if (userData.user_id) localStorage.setItem('uid', userData.user_id);
        } catch (_) {}
        try { localStorage.removeItem('currentEventId'); localStorage.removeItem('currentUserId'); } catch(_) {}
        localStorage.setItem('walkthrough_pending', 'true');
        setShowCreditsPopup(true);
        setTimeout(() => { history.replace('/eventlist'); }, 4000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create vendor');
      }
    } catch (err) {
      
      const msg = err?.response?.data?.message || err?.message || 'Failed to create vendor';
      setToast({ open: true, type: 'error', message: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      field: "photography",
      studio_name: "",
      name: "",
      email: "",
      phone: verifiedPhone,
      state: "",
      gst: "",
    });
    setErrors({});
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setLogoUploading(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const apiKey = process.env.REACT_APP_BROWSER_API_KEY;

      const res = await axios.post(
        `${baseURL}/upload-urls`,
        {
          files: [{ name: file.name, type: file.type || 'image/jpeg' }],
          folder: 'logos',
          bucket: 'original',
          metadata: { asset_type: 'logo' },
        },
        { headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey } }
      );

      const signedUrl = res.data?.uploadUrls?.[0]?.url;
      if (!signedUrl) throw new Error('No signed URL returned');

      await axios.put(signedUrl, file, {
        headers: { 'Content-Type': file.type || 'image/jpeg' },
      });

      const uploaded = res.data?.uploadUrls?.[0];
      const publicUrl = uploaded?.publicUrl || signedUrl.split('?')[0];
      setLogoUrl(publicUrl);
    } catch (err) {
      setToast({ open: true, type: 'error', message: 'Logo upload failed. You can continue without it.' });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoUrl(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleCreditsClose = () => {
    setShowCreditsPopup(false);
    history.replace('/eventlist');
  };

  return (
    <div className="register-wrapper">
      <Toast open={toast.open} type={toast.type} message={toast.message} onClose={() => setToast({ ...toast, open: false })} />

      {showCreditsPopup && (
        <div className="credits-overlay">
          <div className="credits-modal">
            <div className="credits-icon-wrap">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="#FFF3E0"/>
                <text x="12" y="17" textAnchor="middle" fontSize="14">🎉</text>
              </svg>
            </div>
            <h2 className="credits-title">Welcome aboard!</h2>
            <p className="credits-subtitle">Your account has been created successfully.</p>
            <div className="credits-badge">
              <span className="credits-coin">🪙</span>
              <span className="credits-amount">100</span>
              <span className="credits-label">Free Credits Credited!</span>
            </div>
            <p className="credits-note">Use your credits to process and deliver event photos.</p>
            <button className="credits-btn" onClick={handleCreditsClose}>
              Let's Get Started →
            </button>
          </div>
        </div>
      )}

      {/* ── Left Image Panel ── */}
      <div className="register-image-panel">
        <img src="/images/wedding-arch.jpg" alt="Wedding" className="wedding-cover" />
        <div className="image-overlay-text">
          <h3>Your Studio,<br />Your Brand.</h3>
          <p>Register once and manage all your events in one place.</p>
        </div>
      </div>

      {/* ── Right Form Panel ── */}
      <div className="register-form-panel">
        <div className="register-card">

        <div className="card-header">
          <div className="brand-row">
            <img src="/images/logo.png" alt="Easy Events" className="brand-icon" />
            <span className="brand-name">Easy Events</span>
          </div>
          <h2 className="title">Join Easy Events</h2>
          <p className="subtitle">
            Tell us about your studio to create your photographer account.
          </p>
        </div>

        <form className="register-form-box" onSubmit={handleSubmit}>
            
            <div className="field-group">
              <label className="field-label">Studio Name <span style={{ color: '#F80353' }}>*</span></label>
              <input
                type="text"
                name="studio_name"
                className="input-elegant"
                value={formData.studio_name}
                onChange={handleChange}
                placeholder="e.g. Lens & Light Studio"
                required
              />
              {errors.studio_name && <span className="field-error">{errors.studio_name}</span>}
            </div>

            <div className="field-group">
              <label className="field-label">Your Name <span style={{ color: '#F80353' }}>*</span></label>
              <input
                type="text"
                name="name"
                className="input-elegant"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full name"
                required
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>

            <div className="field-group">
              <label className="field-label">Email Address <span style={{ color: '#F80353' }}>*</span></label>
              <input
                type="email"
                name="email"
                className="input-elegant"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>

            <div className="field-group">
              <label className="field-label">Phone Number <span style={{ color: '#F80353' }}>*</span></label>
              <input
                type="text"
                name="phone"
                className="input-elegant"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="10-digit mobile number"
                readOnly
                style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
                required
              />
              {errors.phone && <span className="field-error">{errors.phone}</span>}
            </div>



            <div className="field-group">
              <label className="field-label">State <span style={{ color: '#F80353' }}>*</span></label>
              <select
                name="state"
                className="input-elegant"
                value={formData.state}
                onChange={handleChange}
                required
              >
                <option value="">Select your state</option>
                <option>Andhra Pradesh</option>
                <option>Arunachal Pradesh</option>
                <option>Assam</option>
                <option>Bihar</option>
                <option>Chhattisgarh</option>
                <option>Goa</option>
                <option>Gujarat</option>
                <option>Haryana</option>
                <option>Himachal Pradesh</option>
                <option>Jharkhand</option>
                <option>Karnataka</option>
                <option>Kerala</option>
                <option>Madhya Pradesh</option>
                <option>Maharashtra</option>
                <option>Manipur</option>
                <option>Meghalaya</option>
                <option>Mizoram</option>
                <option>Nagaland</option>
                <option>Odisha</option>
                <option>Punjab</option>
                <option>Rajasthan</option>
                <option>Sikkim</option>
                <option>Tamil Nadu</option>
                <option>Telangana</option>
                <option>Tripura</option>
                <option>Uttar Pradesh</option>
                <option>Uttarakhand</option>
                <option>West Bengal</option>
                <option>Andaman and Nicobar Islands</option>
                <option>Chandigarh</option>
                <option>Dadra and Nagar Haveli and Daman and Diu</option>
                <option>Delhi</option>
                <option>Jammu and Kashmir</option>
                <option>Ladakh</option>
                <option>Lakshadweep</option>
                <option>Puducherry</option>
              </select>
              {errors.state && <span className="field-error">{errors.state}</span>}
            </div>

            <div className="field-group">
              <label className="field-label">GST Number <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
              <input
                type="text"
                name="gst"
                className="input-elegant"
                value={formData.gst}
                onChange={handleGstChange}
                placeholder="e.g. 22ABCDE1234F1Z5"
                maxLength={15}
              />
              {errors.gst && (
                <span className={`field-error${formData.gst.length === 15 ? "" : " field-hint"}`}>
                  {errors.gst}
                </span>
              )}
              {!errors.gst && formData.gst.length === 15 && (
                <span className="field-success">Valid GST number</span>
              )}
            </div>

            <div className="field-group">
              <label className="field-label">
                Studio Logo <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoChange}
              />
              {!logoPreview ? (
                <button
                  type="button"
                  className="reg-logo-upload-btn"
                  onClick={() => logoInputRef.current.click()}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                    <path d="M5 21h14"/>
                  </svg>
                  Upload Studio Logo
                </button>
              ) : (
                <div className="reg-logo-preview-row">
                  <img src={logoPreview} alt="Studio logo" className="reg-logo-preview" />
                  <div className="reg-logo-preview-info">
                    <span className="reg-logo-filename">{logoFile?.name}</span>
                    {logoUploading ? (
                      <span className="reg-logo-status reg-logo-status--uploading">Uploading…</span>
                    ) : logoUrl ? (
                      <span className="reg-logo-status reg-logo-status--done">✓ Uploaded</span>
                    ) : null}
                    <button type="button" className="reg-logo-remove" onClick={handleRemoveLogo}>Remove</button>
                  </div>
                </div>
              )}
            </div>

            <div className="btn-layout">
              <button type="button" className="btn-secondary" onClick={handleReset}>Clear</button>
              <button type="submit" className="btn-primary" disabled={isSubmitting || logoUploading}>
                {isSubmitting ? "Creating Account…" : logoUploading ? "Uploading Logo…" : "Continue"}
              </button>
            </div>
          </form>
      </div>
      </div>
    </div>
  );
}

